<div class="integrations-page">
  <!-- ======================================================
       Integrations dashboard
  ======================================================= -->

  <ng-container *ngIf="!configuringSquare">
    <div class="integrations-intro">
      <div>
        <span class="section-kicker"> Connected Apps </span>

        <h2>Integrations</h2>

        <p>
          Connect the platforms your business already uses and bring your
          existing operations into gloget.
        </p>
      </div>

      <button
        *ngIf="squareConnected"
        type="button"
        class="refresh-button"
        [disabled]="loading"
        (click)="refreshSquareStatus()"
      >
        {{ loading ? "Refreshing..." : "Refresh status" }}
      </button>
    </div>

    <!-- Success -->

    <div *ngIf="successMessage" class="notice notice--success">
      <div class="notice__icon">✓</div>

      <div>
        <strong> Success </strong>

        <p>
          {{ successMessage }}
        </p>
      </div>
    </div>

    <!-- Error -->

    <div *ngIf="errorMessage" class="notice notice--error">
      <div class="notice__icon">!</div>

      <div>
        <strong> Something went wrong </strong>

        <p>
          {{ errorMessage }}
        </p>
      </div>
    </div>

    <!-- Loading -->

    <div *ngIf="loading" class="integration-loading">
      <div class="loading-spinner"></div>

      <div>
        <strong> Checking your integrations </strong>

        <p>Loading your current connection status...</p>
      </div>
    </div>

    <!-- Cards -->

    <div *ngIf="!loading" class="integration-grid">
      <!-- ==================================================
           Square
      =================================================== -->

      <article
        class="integration-card"
        [class.integration-card--connected]="squareConnected"
      >
        <div class="integration-card__top">
          <div class="provider">
            <div class="provider__logo provider__logo--square">
              <span> ■ </span>
            </div>

            <div class="provider__identity">
              <div class="provider__title-row">
                <h3>Square</h3>

                <span
                  class="status-pill"
                  [class.status-pill--connected]="squareConnected"
                >
                  <span class="status-dot"></span>

                  {{ squareConnected ? "Connected" : "Not connected" }}
                </span>
              </div>

              <p>
                Connect your existing Square account to use your business data
                and bookings with gloget.
              </p>
            </div>
          </div>
        </div>

        <!-- ==============================================
             Not connected
        =============================================== -->

        <ng-container *ngIf="!squareConnected">
          <div class="integration-features">
            <div class="feature">
              <span class="feature__check"> ✓ </span>

              <div>
                <strong> Keep your existing setup </strong>

                <p>
                  Continue using Square while introducing gloget into your
                  business.
                </p>
              </div>
            </div>

            <div class="feature">
              <span class="feature__check"> ✓ </span>

              <div>
                <strong> Connect business data </strong>

                <p>
                  Access Square locations, services, staff, customers, and
                  bookings.
                </p>
              </div>
            </div>

            <div class="feature">
              <span class="feature__check"> ✓ </span>

              <div>
                <strong> No immediate migration </strong>

                <p>
                  Your business can adopt gloget without replacing Square on day
                  one.
                </p>
              </div>
            </div>
          </div>

          <div class="integration-card__footer">
            <div class="connection-note">
              <span class="connection-note__icon"> ↗ </span>

              <span>
                You will be securely redirected to Square to authorize gloget.
              </span>
            </div>

            <button
              type="button"
              class="primary-button"
              [disabled]="connecting"
              (click)="connectSquare()"
            >
              <span *ngIf="connecting" class="button-spinner"></span>

              {{ connecting ? "Connecting..." : "Connect Square" }}
            </button>
          </div>
        </ng-container>

        <!-- ==============================================
             Connected
        =============================================== -->

        <ng-container *ngIf="squareConnected && square">
          <div class="connected-account">
            <div class="connected-account__heading">
              <div>
                <span class="connected-label"> Connected account </span>

                <strong>
                  {{ merchantDisplayName }}
                </strong>
              </div>

              <span
                *ngIf="environmentLabel"
                class="environment-pill"
                [class.environment-pill--sandbox]="isSandbox"
              >
                {{ environmentLabel }}
              </span>
            </div>

            <div class="connection-details">
              <div class="connection-detail">
                <span> Merchant ID </span>

                <strong>
                  {{ square.merchant_id }}
                </strong>
              </div>

              <div class="connection-detail">
                <span> Country </span>

                <strong>
                  {{ square.country || "—" }}
                </strong>
              </div>

              <div class="connection-detail">
                <span> Currency </span>

                <strong>
                  {{ square.currency || "—" }}
                </strong>
              </div>

              <div class="connection-detail">
                <span> Square status </span>

                <strong>
                  {{ square.merchant_status || "Connected" }}
                </strong>
              </div>
            </div>
          </div>

          <div class="integration-card__footer">
            <div class="connection-note connection-note--connected">
              <span class="connection-note__icon"> ✓ </span>

              <span>
                gloget is authorized to communicate with this Square business.
              </span>
            </div>

            <div class="integration-actions">
              <button
                type="button"
                class="secondary-button"
                (click)="openSquareSetup()"
              >
                Configure
              </button>

              <button
                type="button"
                class="danger-button"
                [disabled]="disconnecting"
                (click)="disconnectSquare()"
              >
                {{ disconnecting ? "Disconnecting..." : "Disconnect" }}
              </button>
            </div>
          </div>
        </ng-container>
      </article>

      <!-- ==================================================
           Future integrations
      =================================================== -->

      <article class="integration-card integration-card--coming-soon">
        <div class="coming-soon-content">
          <div class="coming-soon-icon">+</div>

          <div>
            <span class="coming-soon-label"> More integrations </span>

            <h3>More ways to connect</h3>

            <p>
              Additional business platforms will be available as gloget's
              integration library grows.
            </p>
          </div>
        </div>

        <span class="coming-soon-pill"> Coming soon </span>
      </article>
    </div>
  </ng-container>

  <!-- ======================================================
       Square setup
  ======================================================= -->

  <app-square-setup
    *ngIf="configuringSquare && squareConnected"
    (closed)="closeSquareSetup()"
  ></app-square-setup>
</div>
